import { useContext, useMemo, useRef } from 'react';

import { AuthContext } from '../../providers/AuthProvider';
import { ApiMethod } from '../models/api-types.type';
import type { NetworkBrand, NetworkCollection, NetworkLink, NetworkLinkStatus, NetworkProduct } from '../models/network.model';
import type {
    BackfillResult,
    BrandDetail,
    BrandInput,
    BrandRow,
    BulkLinkResult,
    CollectionInput,
    DiscoverResult,
    LinkQuery,
    LinkRow,
    LinkUpdateResult,
    MatchVendorResult,
    NetworkStats,
    Paged,
    ProductDetail,
    ProductInput,
    ProductQuery,
    ProductRow,
    RetailerProduct,
    RetailerSummary,
} from './network-admin.types';

const BASE = '/services/pms/admin/network';
const id = (v: string) => encodeURIComponent(v);

/** The super-admin network API (superadmin strategy) — see ws/b/API.md. */
export const NETWORK_ADMIN_ROUTES = {
    stats: `${BASE}/stats`,
    brands: `${BASE}/brands`,
    brand: (brandId: string) => `${BASE}/brands/${id(brandId)}`,
    brandCollections: (brandId: string) => `${BASE}/brands/${id(brandId)}/collections`,
    collection: (collectionId: string) => `${BASE}/collections/${id(collectionId)}`,
    products: `${BASE}/products`,
    product: (productId: string) => `${BASE}/products/${id(productId)}`,
    publish: (productId: string) => `${BASE}/products/${id(productId)}/publish`,
    unpublish: (productId: string) => `${BASE}/products/${id(productId)}/unpublish`,
    promote: `${BASE}/products/promote`,
    retailers: `${BASE}/retailers`,
    retailerProducts: (vendorId: string) => `${BASE}/retailers/${id(vendorId)}/products`,
    matchRun: `${BASE}/match/run`,
    links: `${BASE}/links`,
    link: (linkId: string) => `${BASE}/links/${id(linkId)}`,
    linksBulk: `${BASE}/links/bulk`,
    discover: `${BASE}/discover`,
    backfill: `${BASE}/embeddings/backfill`,
} as const;

/** The most links one `links/bulk` call accepts. */
export const NETWORK_BULK_MAX = 500;

const query = (params: Record<string, string | number | boolean | undefined | null>) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    const s = q.toString();
    return s ? `?${s}` : '';
};

const paged = <T,>(res: any, page: number, limit: number): Paged<T> => ({
    items: (res?.items ?? []) as T[],
    total: Number(res?.total ?? res?.items?.length ?? 0),
    page: Number(res?.page ?? page),
    limit: Number(res?.limit ?? limit),
});

/**
 * The API's own words from a failed request (sendAuthGuardedRequest throws an
 * Error carrying the parsed message and `status`), or null. Pages show it as
 * it is: a 409 "brand still has products" says more than "something went wrong".
 */
export const networkErrorMessage = (e: unknown): string | null => {
    if (e instanceof Error && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    return null;
};

export const useNetworkAdminApi = () => {
    const { sendAuthGuardedRequest } = useContext(AuthContext);
    // The returned object is stable, so pages can put it in effect dependencies;
    // the ref keeps the latest request function (it changes with the session).
    const sendRef = useRef(sendAuthGuardedRequest);
    sendRef.current = sendAuthGuardedRequest;

    return useMemo(() => {
        const send = (method: ApiMethod, path: string, body?: any) => sendRef.current(method, path, body) as Promise<any>;

        return {
            stats: (): Promise<NetworkStats> => send(ApiMethod.GET, NETWORK_ADMIN_ROUTES.stats),

            brands: async (params: { q?: string; status?: string } = {}): Promise<BrandRow[]> => {
                const res = await send(ApiMethod.GET, `${NETWORK_ADMIN_ROUTES.brands}${query(params)}`);
                return (res?.items ?? []) as BrandRow[];
            },
            createBrand: (body: BrandInput): Promise<NetworkBrand> => send(ApiMethod.POST, NETWORK_ADMIN_ROUTES.brands, body),
            brand: (brandId: string): Promise<BrandDetail> => send(ApiMethod.GET, NETWORK_ADMIN_ROUTES.brand(brandId)),
            /** `aliases` replaces the list (the API normalises it and always keeps the name). */
            updateBrand: (brandId: string, patch: BrandInput): Promise<NetworkBrand> => send(ApiMethod.PATCH, NETWORK_ADMIN_ROUTES.brand(brandId), patch),
            /** 409 while the brand still has products. */
            deleteBrand: (brandId: string): Promise<{ deleted: true }> => send(ApiMethod.DELETE, NETWORK_ADMIN_ROUTES.brand(brandId)),

            createCollection: (brandId: string, body: CollectionInput): Promise<NetworkCollection> =>
                send(ApiMethod.POST, NETWORK_ADMIN_ROUTES.brandCollections(brandId), body),
            updateCollection: (collectionId: string, patch: CollectionInput): Promise<NetworkCollection> =>
                send(ApiMethod.PATCH, NETWORK_ADMIN_ROUTES.collection(collectionId), patch),
            /** 409 while the collection still has products. */
            deleteCollection: (collectionId: string): Promise<{ deleted: true }> => send(ApiMethod.DELETE, NETWORK_ADMIN_ROUTES.collection(collectionId)),

            products: async (params: ProductQuery = {}): Promise<Paged<ProductRow>> => {
                const page = params.page ?? 1;
                const limit = params.limit ?? 24;
                const res = await send(
                    ApiMethod.GET,
                    `${NETWORK_ADMIN_ROUTES.products}${query({
                        ...params,
                        digitized: params.digitized === undefined ? undefined : String(params.digitized),
                        page,
                        limit,
                    })}`,
                );
                return paged<ProductRow>(res, page, limit);
            },
            /** By `_id` or `uuid`. */
            product: (productId: string): Promise<ProductDetail> => send(ApiMethod.GET, NETWORK_ADMIN_ROUTES.product(productId)),
            createProduct: (body: ProductInput & { brandId: string; name: string }): Promise<NetworkProduct> =>
                send(ApiMethod.POST, NETWORK_ADMIN_ROUTES.products, body),
            updateProduct: (productId: string, patch: ProductInput): Promise<NetworkProduct> => send(ApiMethod.PATCH, NETWORK_ADMIN_ROUTES.product(productId), patch),
            publishProduct: (productId: string): Promise<NetworkProduct> => send(ApiMethod.POST, NETWORK_ADMIN_ROUTES.publish(productId)),
            unpublishProduct: (productId: string): Promise<NetworkProduct> => send(ApiMethod.POST, NETWORK_ADMIN_ROUTES.unpublish(productId)),
            /** A retailer's product becomes a network product, linked to it. 409 when it already has a confirmed link. */
            promote: (body: { vendorId: string; productUuid: string; brandId: string; collectionId?: string }): Promise<{ product: NetworkProduct; link: NetworkLink }> =>
                send(ApiMethod.POST, NETWORK_ADMIN_ROUTES.promote, body),

            retailers: async (): Promise<RetailerSummary[]> => {
                const res = await send(ApiMethod.GET, NETWORK_ADMIN_ROUTES.retailers);
                return (res?.items ?? []) as RetailerSummary[];
            },
            retailerProducts: async (vendorId: string, params: { q?: string; unlinked?: boolean; page?: number; limit?: number } = {}): Promise<Paged<RetailerProduct>> => {
                const page = params.page ?? 1;
                const limit = params.limit ?? 24;
                const res = await send(
                    ApiMethod.GET,
                    `${NETWORK_ADMIN_ROUTES.retailerProducts(vendorId)}${query({ q: params.q, unlinked: params.unlinked ? 'true' : undefined, page, limit })}`,
                );
                return paged<RetailerProduct>(res, page, limit);
            },

            /** Every retailer when `vendorId` is absent. */
            runMatching: async (vendorId?: string): Promise<MatchVendorResult[]> => {
                const res = await send(ApiMethod.POST, NETWORK_ADMIN_ROUTES.matchRun, vendorId ? { vendorId } : {});
                return (res?.results ?? []) as MatchVendorResult[];
            },

            links: async (params: LinkQuery = {}): Promise<Paged<LinkRow>> => {
                const page = params.page ?? 1;
                const limit = params.limit ?? 20;
                const res = await send(ApiMethod.GET, `${NETWORK_ADMIN_ROUTES.links}${query({ ...params, page, limit })}`);
                return paged<LinkRow>(res, page, limit);
            },
            setLinkStatus: (linkId: string, status: NetworkLinkStatus): Promise<LinkUpdateResult> => send(ApiMethod.PATCH, NETWORK_ADMIN_ROUTES.link(linkId), { status }),
            /** At most NETWORK_BULK_MAX ids per call. */
            bulkLinks: (ids: string[], status: 'confirmed' | 'rejected'): Promise<BulkLinkResult> => send(ApiMethod.POST, NETWORK_ADMIN_ROUTES.linksBulk, { ids, status }),

            discover: (dryRun: boolean): Promise<DiscoverResult> => send(ApiMethod.POST, NETWORK_ADMIN_ROUTES.discover, { dryRun }),
            backfillEmbeddings: (vendorId?: string): Promise<BackfillResult> => send(ApiMethod.POST, NETWORK_ADMIN_ROUTES.backfill, vendorId ? { vendorId } : {}),
        };
    }, []);
};

export type NetworkAdminApi = ReturnType<typeof useNetworkAdminApi>;
